/**
 * English strings of the game screens (milestone 3). Same rule as en.ts: `game-it.ts` is typed
 * against this shape, so a missing or extra key is a compile error.
 */
export const gameEn = {
  setup: {
    you: 'You',
    soloTitle: 'Solo heist',
    soloIntro: 'You and the bots. You are always a thief; the Mole, if there is one, is a bot.',
    players: 'Players',
    playersOption: '{{count}} players',
    map: 'Heist',
    difficulty: 'Difficulty',
    infiltrator: 'The Mole',
    infiltratorOptions: { never: 'Off', always: 'Always', normal: 'By chance' },
    infiltratorHint: {
      never: 'Nobody works against you.',
      always: 'One of the bots is the Mole.',
      normal: 'Maybe, as in online games: 40 %, 55 % or 70 % with 3, 4 or 5 players.',
    },
    role: 'Your role',
    roleRandom: 'Random',
    botLevel: 'Bot level',
    botLevels: {
      auto: 'By difficulty',
      easy: 'Easy',
      medium: 'Medium',
      hard: 'Hard',
      expert: 'Expert',
    },
    start: 'Start the heist',
  },
  game: {
    you: 'you',
    hud: {
      leave: 'Leave the heist',
      timer: 'Time left',
      alarm: 'Alarm',
      faster: 'clock at {{pct}} %',
      pause: 'Pause',
      resume: 'Resume',
    },
    pause: {
      title: 'Paused',
      body: 'The clock and the bots are on hold.',
      leave: 'Leave',
      resume: 'Back to the job',
    },
    leave: {
      title: 'Leave the heist?',
      body: 'The job is lost and nothing is saved.',
      stay: 'Stay',
      confirm: 'Leave',
    },
    phase: {
      briefing: 'Briefing',
      planning: 'Planning',
      execution: 'The job',
      escape: 'The getaway',
      hideout: 'The hideout',
      reveal: 'The reveal',
      ended: 'The reveal',
    },
    difficulty: { easy: 'Easy', normal: 'Normal', pro: 'Professional', legend: 'Legend' },
    roles: {
      mastermind: 'Mastermind',
      locksmith: 'Locksmith',
      hacker: 'Hacker',
      lookout: 'Lookout',
      driver: 'Driver',
    },
    roleInfo: {
      mastermind: {
        sees: 'The whole blueprint: rooms, patrols, cameras, how long each room will take.',
        does: 'Proposes the plan, guides the team, holds the heights of the lock and the safe owner’s profile.',
        powers: 'Two Plan Bs: undo the last mistake and its alarm.',
      },
      locksmith: {
        sees: 'Only the room you are in.',
        does: 'Picks locks, cracks safes, turns the mirrors of the laser.',
        powers: 'None. Your hands are the tool.',
      },
      hacker: {
        sees: 'Terminals, the network, the wiring.',
        does: 'Electrical panels, passwords, cameras, the laser preview; drives the getaway in teams of three or four.',
        powers: 'Three Loops: the cameras stop counting for 20 seconds.',
      },
      lookout: {
        sees: 'The guards’ radar, live.',
        does: 'Holds the guards’ route and the panel manual. Moves through the corridors with the others.',
        powers: 'Two Coins and one Firecracker to lure the guards.',
      },
      driver: {
        sees: 'The police radio.',
        does: 'Drives the getaway and passes on the radio bulletins.',
        powers: 'None. Your ears are the tool.',
      },
    },
    maps: {
      bank: {
        name: 'The Nocturne Bank',
        place: 'Milan, the 1960s. An Art Nouveau bank, a vault under the street.',
        special: 'No special rule.',
      },
      museum: {
        name: 'The Museum of Masks',
        place: 'Venice, a night of Carnival. The party hides a lot.',
        special: 'Masquerade: on the main floor the guards see one tile less.',
      },
      villa: {
        name: 'The Cliffside Villa',
        place: 'The coast, an eccentric collector and his contraptions.',
        special: 'Fewer guards in the corridors, more devices in the rooms.',
      },
      express: {
        name: 'The Alpine Express',
        place: 'A night train. The vault is the armored mail car.',
        special: 'Timer x0.8. Every 90 seconds a tunnel: radar, radio and cameras go quiet for 10.',
      },
    },
    rooms: {
      bank: {
        skylight: 'Skylight',
        directorOffice: 'Director’s Office',
        archive: 'Archive',
        gallery: 'Art Nouveau Gallery',
        atrium: 'Glass Atrium',
        tellers: 'Tellers’ Hall',
        serviceYard: 'Service Yard',
        countingRoom: 'Counting Room',
        powerRoom: 'Power Room',
        armoredCorridor: 'Armored Corridor',
        safeDeposit: 'Safe Deposit Boxes',
        vault: 'Vault',
        sewerTunnel: 'Sewer Tunnel',
      },
      museum: {
        attic: 'Attic Hatch',
        costumeLoft: 'Costume Loft',
        maskRoom: 'Hall of Masks',
        ballroom: 'Ballroom',
        portraitGallery: 'Portrait Gallery',
        grandStair: 'Grand Staircase',
        ticketOffice: 'Ticket Office',
        serviceLane: 'Service Lane',
        restorationLab: 'Restoration Lab',
        controlRoom: 'Control Room',
        treasureCrypt: 'Treasure Crypt',
        undergroundCanal: 'Underground Canal',
        jewelRoom: 'Jewel Room',
      },
      villa: {
        terrace: 'Panoramic Terrace',
        greenhouse: 'Greenhouse',
        wonderSalon: 'Salon of Wonders',
        library: 'Library',
        collectorStudy: 'Collector’s Study',
        statueGallery: 'Statue Gallery',
        kitchen: 'Kitchen and Pantry',
        serviceEntrance: 'Service Entrance',
        cellar: 'Cellar',
        controlRoom: 'Switchboard Room',
        rarityVault: 'Rarities Vault',
        seaCave: 'Sea Cave',
        stampRoom: 'Stamp Room',
      },
      express: {
        tailPlatform: 'Tail Platform',
        sleeper: 'Sleeping Car',
        panoramaCar: 'Panoramic Lounge',
        diningCar: 'Dining Car',
        baggageCar: 'Baggage Car',
        mailCar: 'Armored Mail Car',
        sleeperRoof: 'Lounge Roof',
        diningRoof: 'Dining Car Roof',
        baggageRoof: 'Baggage Car Roof',
        kitchen: 'Galley',
        conductorCab: 'Conductor’s Cab',
        undercarriage: 'Undercarriage',
        presidentialCabin: 'Presidential Cabin',
      },
    },
    entrances: {
      roof: { name: 'Roof', effect: 'No alarm. The timer starts 60 seconds shorter.' },
      sewer: { name: 'Sewers', effect: 'No alarm. The first puzzle is one level harder.' },
      service: {
        name: 'Service door',
        effect: 'Alarm starts at 10 %. The first puzzle is one level easier.',
      },
    },
    gear: {
      proPick: { name: 'Pro pick', effect: 'A lock has one pin fewer.' },
      jammer: { name: 'Jammer', effect: 'Switches off the cameras of one room.' },
      rope: { name: 'Rope', effect: 'Allows one rope jump between floors.' },
      disguise: { name: 'Disguise', effect: 'The first time a guard sees you, no alarm.' },
      drone: { name: 'Drone', effect: 'For 30 seconds everybody sees the next room.' },
      emp: { name: 'EMP kit', effect: 'Solves one electrical panel.' },
    },
    puzzles: {
      lock: {
        name: 'Pin lock',
        short: 'Lock',
        goal: 'Set the pins in the right order, each at the right height. The order is on the hacker’s terminal, the heights on the mastermind’s plan.',
      },
      safe: {
        name: 'Safe',
        short: 'Safe',
        goal: 'Find the combination. The owner’s profile narrows it down; the safe answers each try with clicks (right digit, right place) and hums (right digit, wrong place).',
      },
      panel: {
        name: 'Electrical panel',
        short: 'Panel',
        goal: 'Cut or connect each wire as the manual says. The manual is not on your screen.',
      },
      cameras: {
        name: 'Cameras',
        short: 'Cams',
        goal: 'Turn every camera away from the guards’ path, then run the check. The path is on someone else’s screen.',
      },
      laser: {
        name: 'Laser grid',
        short: 'Laser',
        goal: 'Steer the beam to the receiver without touching the display case. The locksmith turns the mirrors; the hacker sees the beam.',
      },
      password: {
        name: 'Password',
        short: 'Pass',
        goal: 'Pick the right word. The clues about it are spread among the others.',
      },
      corridor: {
        name: 'Guarded corridor',
        short: 'Corridor',
        goal: 'Everybody moves one step, then the guards move. Get every token to the exit without being seen.',
      },
      escape: {
        name: 'Getaway',
        short: 'Getaway',
        goal: 'Slide the blocks to free the getaway car (★) to the right edge. Police cars block the way.',
      },
      vault: {
        name: 'Vault code',
        short: 'Vault',
        goal: 'Five symbols; each of you knows one or two. Punch them in, then everybody confirms. A third wrong code locks the vault for good.',
      },
    },
    planProblem: {
      noEntrance: 'Pick an entrance first.',
      noVault: 'The route must end in the vault.',
      tooShort: 'A route has at least four rooms.',
      needRope: 'A rope jump needs the Rope in the kit.',
      budget: 'The kit costs more than the budget.',
      other: 'This is not a valid plan yet.',
    },
    briefing: {
      yourRole: 'Your role',
      sees: 'You see',
      does: 'You do',
      powers: 'Powers',
      secret: 'Secret',
      stamp: 'Infiltrator',
      infiltratorBody:
        'You are the Mole. You win if the heist fails, or if the others do not vote you out. You may sabotage three times, and lie. Do not look too clever.',
      coverBody: 'A face-down card. One of you may be working against the team. Or nobody.',
      target: 'The target',
      loot: 'Loot {{amount}}',
      players: '{{count}} thieves',
      team: 'The crew',
      ready: 'Ready',
      waiting: 'Waiting for the others',
      skip: 'The briefing ends when everybody is ready.',
    },
    planning: {
      title: 'The blueprint',
      proposal: 'The proposal',
      notVoted: 'thinking',
      yes: 'yes',
      no: 'no',
      approve: 'Approve',
      reject: 'Reject',
      tie: 'A tie. You decide.',
      revise: 'Revise',
      waitingTie: 'A tie: the mastermind decides.',
      rejected: 'Rejected {{count}} time(s). A new plan is coming.',
      waiting: 'The {{who}} is drawing up the plan.',
      entrance: 'Entrance',
      route: 'Route',
      alcoves: 'Alcoves',
      gear: 'Kit',
      none: 'none',
      estimate: 'Work and time',
      estimateValue: 'about {{work}} of work against {{timer}} on the clock',
      stamp: 'Approved',
      build: 'Draw the plan',
      howTo:
        'Tap rooms to extend the route to the vault; tap a room already on it to cut back; tap a dashed alcove to add it.',
      pickEntrance: 'Pick an entrance to start.',
      gearTitle: 'Kit {{spent}} / {{budget}}',
      propose: 'Propose the plan',
      clear: 'Start over',
      suggest: 'The bots’ idea',
    },
    feed: { title: 'The line' },
    route: {
      label: 'The plan',
      corridor: 'corridor to {{room}}',
      rope: 'rope jump',
      back: 'back from {{room}}',
      escape: 'Getaway',
    },
    heist: {
      puzzles: 'Puzzles in this room',
      moving: 'The team is moving to the next room.',
    },
    power: {
      title: 'Powers',
      planB: 'Plan B ({{count}} left)',
      drone: 'Fly the drone',
      skip: 'Skip the next alcove',
      loop: 'Loop the cameras ({{count}} left)',
      mole: 'The Mole’s tools',
    },
    sabotage: {
      tamper: 'Tamper with a clue',
      tipoff: 'Tip off the guards',
      rush: 'Rush the patrol',
    },
    phrases: { open: 'Say something', pings: 'Pings', say: 'Say it', param: 'Value {{n}}' },
    ping: { here: 'Here', danger: 'Danger', wait: 'Wait', go: 'Go' },
    escapeSides: { left: 'left', right: 'right', top: 'top', bottom: 'bottom' },
    errors: {
      generic: 'That did not work.',
      gameOver: 'The game is over.',
      wrongPhase: 'Not now.',
      notAllowed: 'That is not your job.',
      invalid: 'That is not allowed.',
      unknownPuzzle: 'No such puzzle.',
      puzzleNotActive: 'The team is not there yet.',
      puzzleSolved: 'Already solved.',
      illegalMove: 'That move is not possible.',
      noCharges: 'None left.',
      cooldown: 'Too soon.',
      nothingToDo: 'Nothing to do there.',
      tooLate: 'Too late for that.',
    },
    puzzle: {
      say: 'Say it',
      heard: 'What you were told',
      heardNone: 'Nothing yet. Ask the others.',
      bystander: 'The {{role}} works the {{puzzle}}. You can still help with phrases and pings.',
      solved: 'Solved',
      yours: 'Your turn',
      done: 'Done.',
      useGear: 'Use {{gear}}',
      lock: {
        progress: 'Pins set: {{done}} of {{total}}',
        pins: 'Pins',
        set: 'Pin {{pin}} at height {{height}}',
        vibrates: 'Pin {{pin}} vibrates: right pin, wrong height.',
        falls: 'Pin {{pin}} falls back.',
        orderHint: 'The pins must go in this order. Tell the locksmith the next one.',
        heightsHint: 'The height of each pin. Tell the locksmith when asked.',
      },
      safe: {
        left: 'Tries left: {{count}}',
        repeats: 'digits may repeat',
        distinct: 'all digits differ',
        keypad: 'Keypad',
        erase: 'Erase',
        jammed: 'Jammed',
        try: 'Try',
        answer: '{{clicks}} clicks, {{hums}} hums',
        profileHint: 'The owner’s profile. Every line is true. Read them out to the locksmith.',
      },
      panel: {
        serial: 'Serial number',
        done: 'Done',
        cut: 'Cut',
        connect: 'Connect',
        manualHint: 'The panel manual, rule by rule. The first rule that fits a wire decides.',
      },
      cameras: {
        board: 'Camera grid',
        camera: 'Camera {{n}} points {{dir}}',
        turn: 'Turn camera {{n}}',
        locked: 'Network locked',
        verify: 'Check',
        pathHint:
          'The guards’ path (numbered) and the cameras. Tell the hacker which way each camera must not point.',
        pathBoard: 'Guards’ path',
      },
      laser: {
        hint: 'Tap a mirror to flip it, then fire. The beam must reach the receiver and miss the case.',
        board: 'Laser room',
        mirror: 'Mirror {{n}}, {{orient}}',
        fire: 'Fire',
        recharging: 'Recharging',
        previewHint:
          'Where the beam goes with the mirrors as they are now. Tell the locksmith what to turn.',
        preview: 'Beam preview',
        end: {
          receiver: 'The beam reaches the receiver.',
          case: 'The beam hits the display case.',
          obstacle: 'The beam stops at an obstacle.',
          edge: 'The beam leaves the room.',
          loop: 'The beam goes round and round.',
        },
      },
      password: {
        left: 'Tries left: {{count}}',
        locked: 'The terminal is locked for a moment.',
        clueHint: 'What you know about the word. Read it out to the hacker.',
      },
      corridor: {
        round: 'Round {{n}}',
        board: 'Corridor',
        move: 'Move',
        north: 'Up',
        south: 'Down',
        east: 'Right',
        west: 'Left',
        stay: 'Stay',
        waiting: 'Waiting for the others.',
        coin: 'Coin ({{count}})',
        petard: 'Firecracker ({{count}})',
        aim: 'Tap a cell.',
        radarHint: 'The radar: where each guard goes next. Say it before everybody moves.',
      },
      escape: {
        yours: 'You drive. Tap a block, then slide it. Free the car (★).',
        watch: 'The {{who}} drives. Watch the board and call out what you see.',
        moves: 'Moves: {{n}}',
        board: 'Parking lot',
        car: 'Getaway car',
        police: 'Police car {{n}}',
        block: 'Block {{n}}',
        slide: 'Slide',
        radio: 'Radio: a police car from the {{side}} in {{when}}.',
      },
      vault: {
        errors: 'Wrong codes: {{n}} of {{max}}',
        locked: 'locked',
        mine: 'Your symbols',
        theirs: 'Their symbols',
        code: 'The code',
        slot: 'Position {{n}}: {{symbol}}',
        empty: 'empty',
        symbols: 'Symbols',
        confirm: 'Confirm the code',
        unconfirm: 'Take back my confirmation',
        sayConfirm: 'Say “confirming”',
        in: 'in',
        waiting: 'waiting',
      },
    },
    events: {
      ping: {
        here: '{{who}}: here.',
        danger: '{{who}}: danger!',
        wait: '{{who}}: wait.',
        go: '{{who}}: go!',
      },
      notice: {
        suspiciousGuard: 'A guard looks suspicious.',
        interference: 'Interference on the line.',
        patrolEarly: 'The patrol is early!',
      },
      glitch: 'Your terminal flickers.',
      alarm: 'Alarm +{{delta}} → {{total}} %',
      police: 'The police are on their way: the clock runs faster.',
      alcovesClosed: 'The alcoves are sealed.',
      solved: '{{puzzle}} solved.',
      roomCleared: '{{room}} is clear.',
      entering: 'Into {{room}}.',
      corridor: 'A guarded corridor.',
      skipped: '{{room}} skipped.',
      rope: 'A jump on the rope.',
      sighted: 'A guard saw someone!',
      planProposed: 'A plan is on the table.',
      planVote: 'Vote: {{yes}} for, {{no}} against.',
      planApproved: 'Plan approved.',
      planRejected: 'Plan rejected.',
      planTie: 'A tie.',
      started: 'The clock is running.',
      escape: 'The vault is open. Run!',
      radio: 'Radio: a police car from the {{side}}.',
      reinforcement: 'A police car blocks the lot.',
      bulletin: 'Radio: “{{kind}}”',
      gearUsed: '{{gear}} used.',
      disguise: 'The disguise saved you.',
      loop: 'The cameras are looped.',
      planB: 'Plan B: the last mistake never happened.',
      tunnel: 'A tunnel: radar, radio and cameras go quiet.',
      failed: {
        alarm: 'The alarm is at 100 %. The heist is lost.',
        time: 'Out of time. The heist is lost.',
        vault: 'The vault is locked for good. The heist is lost.',
      },
      completed: 'You made it to the hideout.',
      expelled: '{{who}} is out.',
      nobodyOut: 'Nobody is out.',
      splitProposed: '{{who}} proposes a split.',
      splitApproved: 'The split is approved.',
      splitRejected: 'The split is rejected.',
      splitEqual: 'Too many refusals: equal shares.',
    },
    hideout: {
      stage: {
        discussion: 'Discussion',
        accusation: 'Accusation',
        split: 'The split',
      },
      intro: {
        discussion: 'The loot is on the table. Talk, and pin the one you trust least.',
        accusation: 'A secret vote. A majority throws one of you out. Or nobody.',
        split: 'The mastermind proposes the shares; a majority must agree.',
      },
      board: 'The suspects’ board',
      pinsOn: '{{count}} pin(s)',
      pin: 'Pin',
      unpin: 'Unpin',
      accuse: 'Who is the Mole?',
      out: 'You are out.',
      voteCast: 'Your vote is in.',
      accusePlayer: 'Accuse {{name}}',
      nobody: 'Nobody',
      voted: '{{count}} of {{total}} have voted.',
      splitYours: 'Your split',
      total: 'Total: {{total}} %',
      propose: 'Propose',
      equalSplit: 'Equal shares',
      splitTitle: 'The split',
      proposer: '{{name}} proposes.',
      rejections: 'Refused {{count}} time(s).',
      accept: 'Accept',
      refuse: 'Refuse',
      waitingVotes: 'Waiting for the votes.',
      waitingProposal: 'Waiting for the proposal.',
    },
    reveal: {
      waiting: 'The newsreel is loading…',
      success: 'The heist was a success',
      failed: {
        alarm: 'The alarm brought the police',
        time: 'The time ran out',
        vault: 'The vault locked for good',
      },
      youWon: 'You won.',
      youLost: 'You lost.',
      infiltrator: 'The Mole was {{name}}.',
      noInfiltrator: 'There was no Mole.',
      expelled: '{{name}} was voted out.',
      loot: 'The loot',
      base: 'Target',
      extras: 'Alcoves',
      penalty: 'Alarm at {{alarm}} %',
      total: 'Total',
      nothing: 'Nothing. The plan promised {{planned}}.',
      scores: 'Winners and shares',
      mole: 'Mole',
      winner: 'Wins',
      score: '{{amount}}',
      timeline: 'What really happened',
      noEvents: 'Nothing strange happened.',
      sabotage: '{{who}} sabotaged: {{what}}',
      chance: 'Chance: {{what}}',
      headline: {
        moleCaught: 'Inspector unmasks {{name}}',
        moleFree: '{{name}}, the Mole, walks free',
        innocentOut: 'An innocent is thrown out: {{name}}',
        noMole: 'No traitor among the thieves',
      },
      reel: 'The Night Newsreel',
      again: 'Play again',
      menu: 'Back to the menu',
    },
  },
  training: {
    title: 'Training',
    intro:
      'One puzzle at a time, against the clock. Everything the team would tell you is on the table.',
    puzzle: 'Puzzle',
    level: 'Level',
    levelOption: 'Level {{n}}',
    levelHint: 'Higher levels mean bigger grids, more guards and longer codes.',
    solver: 'Worked by the {{role}}',
    par: 'Par time {{time}}',
    record: 'Record: {{time}}',
    noRecord: 'No record yet',
    board: {
      title: 'Fastest times',
      hint: 'The best time of each player at this puzzle and level. Guests are not listed.',
      empty: 'Nobody has a time here yet. Yours could be the first.',
      failed: 'The board could not be loaded.',
      guest: 'Turn your guest account into a real one to put your times on the board.',
      signIn: 'Sign in to see the fastest times and put yours on the board.',
      you: 'you',
    },
    starsLabel: '{{count}} of 3 stars',
    start: 'Start',
    hudTitle: '{{puzzle}} · level {{level}}',
    yours: 'Your puzzle',
    team: 'What the others know',
    teamHint: 'In a heist this is on their screens. Tap Say to relay it, as they would.',
    teamNothing: 'Nobody else holds a clue for this one.',
    timeLeft: 'Time left',
    verdict: {
      won: 'Solved',
      time: 'Time is up',
      alarm: 'The alarm went off',
      vault: 'The vault is locked for good',
    },
    result: {
      time: 'Time',
      par: 'Par',
      errors: 'Mistakes',
      alarm: 'Alarm',
      stars: 'Stars',
      newRecord: 'New record!',
    },
    again: 'Try again',
    another: 'Another puzzle',
    menu: 'Back to the menu',
  },
  tutorial: {
    title: 'Tutorial',
    intro:
      'Five short heists on the Nocturne Bank, one role at a time. Bots play the others, patiently, and a coach explains what is on your screen.',
    chapter: 'Chapter {{n}}',
    done: 'Done',
    start: 'Start',
    replay: 'Play again',
    chapters: {
      c1: { title: 'The Locksmith', lesson: 'Set the pins of a lock as your teammates tell you.' },
      c2: {
        title: 'The Hacker',
        lesson: 'Cut the wires of an electrical panel and aim the cameras.',
      },
      c3: { title: 'The Mastermind', lesson: 'Draw the plan, win the vote and guide the safe.' },
      c4: {
        title: 'The Lookout',
        lesson: 'Watch the guards and lead the team through a corridor.',
      },
      c5: {
        title: 'The Driver',
        lesson: 'Open the vault, drive the getaway and face the hideout.',
      },
    },
    coach: { title: 'Coach', gotIt: 'Got it', show: 'Show the tip' },
    complete: {
      title: 'Chapter {{n}} done',
      body: 'You have learned what this role is for. Keep going in the same heist, or move on.',
      bodyLast: 'That was the last chapter. You know every role now.',
      next: 'Next chapter',
      keep: 'Keep playing',
      menu: 'Back to the chapters',
    },
    tips: {
      briefing: {
        c1: 'You are the Locksmith. Your hands are the tool: you open locks, crack safes and turn the laser mirrors. You never see the secrets, your teammates tell you. Read your card, then tap Ready.',
        c2: 'You are the Hacker. You work electrical panels, cameras and passwords, and you have three Loops to blind the cameras for a while. You never hold the whole truth: the others tell you. Read your card, then tap Ready.',
        c3: 'You are the Mastermind. You see the whole blueprint, you draw the plan and you hold the numbers the others need: lock heights, the safe owner’s profile. Read your card, then tap Ready.',
        c4: 'You are the Lookout. Only you see the guards’ radar, and you hold the panel manual and the cameras’ paths. Read your card, then tap Ready.',
        c5: 'You are the Driver. You open the vault with the others, then you drive the getaway. Read your card, then tap Ready.',
      },
      vote: 'The Mastermind has put a plan on the table: an entrance, the rooms in order, the kit. Look at it on the blueprint and approve it. A rejected plan costs time.',
      build:
        'You draw the plan. Pick an entrance, then tap rooms from there up to the Vault (the star). Check the estimate against the clock. Tap “The bots’ idea” for a plan that goes through the Tellers, where there is a safe, then press Propose.',
      voting: 'The plan is on the table. Your teammates vote: a majority is enough.',
      wait: 'This one belongs to someone else. Stay ready: you can still help with “Say something”.',
      lock: 'A pin lock. Every pin goes at one height and they must be set in order. The Hacker knows the order, the Mastermind the heights, and you know neither: listen to the line, then tap the height under the pin. A wrong move raises the alarm. If you missed something, ask: “Say something”.',
      panel:
        'An electrical panel. Each wire is cut or connected according to a manual that is not on your screen: with three players the Mastermind holds it, otherwise the Lookout. Ask for the rules and the serial number with “Say something”, then act wire by wire.',
      cameras:
        'The cameras. Turn each one away from the guards’ path, then press Check. The path is on someone else’s screen: ask for it. While cameras are on, the alarm creeps up.',
      laser:
        'A laser grid. The Locksmith turns the mirrors, you see where the beam goes. Say where it ends and which mirror to flip.',
      safe: 'A safe. You hold the owner’s profile: say what you know with the quick phrases. The Locksmith tries combinations and the safe answers with clicks and hums.',
      help: 'Nothing for you to do here. Your Plan B can undo the last mistake, and the quick phrases keep the team in sync.',
      manual:
        'The panel and the cameras are worked by the Hacker, but the manual and the guards’ paths are with you. Say them: “Say something”, then pick the phrase.',
      corridor:
        'A guarded corridor. Everybody moves one step at a time and the guards walk their patrols. Only your radar shows the whole patrol: tell the others where to wait and when to go. Use a Coin or the Firecracker to lure a guard away.',
      vault:
        'The vault. The five symbols of the code are split among you: say yours, collect the others, enter the code and let everyone confirm. Three wrong codes lock it for good.',
      escape:
        'The getaway. You drive: slide the blocks to free the car. The police radio announces cars from the left or the right: pass it on to the team.',
      hideout:
        'The hideout. Talk, pin your suspicions, vote, and split the loot. In the tutorial there is no traitor, so you can only practise.',
    },
  },
  phrases: {
    names: {
      ready: 'Ready',
      wait: 'Wait',
      go: 'Go',
      danger: 'Danger',
      needRole: 'I need…',
      done: 'Done',
      repeat: 'Say again',
      dontKnow: 'I don’t know',
      lockPinHeight: 'Pin height',
      lockNextPin: 'Next pin',
      safeDigit: 'Digit is…',
      safeClue: 'Code contains',
      safeNo: 'Code has no',
      safeSum: 'Sum of digits',
      safeParity: 'Digit even / odd',
      safeGreater: 'Digit bigger than',
      safeAttempt: 'My try',
      panelSerial: 'Serial number',
      panelWire: 'Wire action',
      panelRule: 'Manual rule',
      panelRuleLetter: 'Manual rule (letter)',
      camAvoid: 'Camera: avoid',
      camNow: 'Camera points',
      laserMirror: 'Mirror',
      laserEnds: 'Beam ends',
      passClue: 'Word clue',
      passTry: 'Trying word',
      corGuard: 'Guard goes',
      corHold: 'Hold still',
      corShadow: 'Shadow at',
      escReinforce: 'Reinforcements',
      escMove: 'Move block',
      vaultPos: 'Vault position',
      vaultConfirm: 'Confirming',
      bulletin: 'Radio bulletin',
      suspect: 'I suspect',
      trust: 'I trust',
      sawWeird: 'My terminal glitched',
    },
    ready: 'Ready.',
    wait: 'Wait.',
    go: 'Go!',
    danger: 'Danger!',
    needRole: 'I need the {{p1}}.',
    done: 'Done.',
    repeat: 'Say again?',
    dontKnow: 'I don’t know.',
    lockPinHeight: 'Pin {{p1}}: height {{p2}}.',
    lockNextPin: 'Next is pin {{p1}}.',
    safeDigit: 'Digit {{p1}} is {{p2}}.',
    safeClue: 'The code contains a {{p1}}.',
    safeNo: 'The code has no {{p1}}.',
    safeSum: 'The digits add up to {{p1}}.',
    safeParity: 'Digit {{p1}} is {{p2}}.',
    safeGreater: 'Digit {{p1}} is bigger than digit {{p2}}.',
    safeAttempt: 'Tried {{p1}}: {{p2}} clicks, {{p3}} hums.',
    panelSerial: 'The serial number ends in {{p1}}.',
    panelWire: '{{p1}} wire: {{p2}}.',
    panelRule_color: 'Rule {{p1}}: {{p3}} wire → {{p4}}.',
    panelRule_odd: 'Rule {{p1}}: {{p3}} wire, serial ends odd → {{p4}}.',
    panelRule_even: 'Rule {{p1}}: {{p3}} wire, serial ends even → {{p4}}.',
    panelRule_first: 'Rule {{p1}}: first wire → {{p4}}.',
    panelRule_last: 'Rule {{p1}}: last wire → {{p4}}.',
    panelRule_twoPlus: 'Rule {{p1}}: two or more {{p3}} wires → {{p4}}.',
    panelRuleLetter: 'Rule {{p1}}: {{p2}} wire, letter {{p3}} in the serial → {{p4}}.',
    camAvoid: 'Camera {{p1}}: do not point {{p2}}.',
    camNow: 'Camera {{p1}} points {{p2}}.',
    laserMirror: 'Mirror {{p1}}: {{p2}}.',
    laserEnds: 'The beam ends at {{p1}}, {{p2}}.',
    passClue_category: 'Word clue: it is a {{p2}}.',
    passClue_first: 'Word clue: it starts with {{p2}}.',
    passClue_last: 'Word clue: it ends with {{p2}}.',
    passClue_contains: 'Word clue: it contains {{p2}}.',
    passClue_excludes: 'Word clue: it has no {{p2}}.',
    passTry: 'Trying word {{p1}}.',
    corGuard: 'Guard {{p1}} goes {{p2}}.',
    corHold: '{{p1}}: hold still.',
    corShadow: 'Shadow at {{p1}}, {{p2}}.',
    escReinforce: 'Reinforcements from the {{p1}} in {{p2}} s.',
    escMove: 'Move block {{p1}}.',
    vaultPos: 'Position {{p1}} is {{p2}}.',
    vaultConfirm: 'Confirming.',
    bulletin: 'Radio: expect “{{p1}}”.',
    suspect: 'I suspect {{p1}}.',
    trust: 'I trust {{p1}}.',
    sawWeird: 'My terminal glitched.',
    enum: {
      n: 'north',
      ne: 'north-east',
      e: 'east',
      se: 'south-east',
      s: 'south',
      sw: 'south-west',
      w: 'west',
      nw: 'north-west',
      red: 'red',
      blue: 'blue',
      yellow: 'yellow',
      black: 'black',
      white: 'white',
      green: 'green',
      any: 'any',
      cut: 'cut',
      connect: 'connect',
      leave: 'leave alone',
      even: 'even',
      odd: 'odd',
      slash: '/',
      backslash: '\\',
      left: 'left',
      right: 'right',
      top: 'top',
      bottom: 'bottom',
      mastermind: 'mastermind',
      locksmith: 'locksmith',
      hacker: 'hacker',
      lookout: 'lookout',
      driver: 'driver',
      suspiciousGuard: 'a guard looks suspicious',
      interference: 'interference on the line',
      patrolEarly: 'the patrol is early',
      color: 'colour',
      first: 'first',
      last: 'last',
      twoPlus: 'two or more',
      category: 'category',
      contains: 'contains',
      excludes: 'excludes',
    },
    categories: { '0': 'place', '1': 'tool', '2': 'person', '3': 'loot', '4': 'vice' },
    symbols: {
      '0': 'spade',
      '1': 'heart',
      '2': 'diamond',
      '3': 'club',
      '4': 'star',
      '5': 'moon',
      '6': 'sun',
      '7': 'anchor',
    },
  },
};

type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };

/** Shape the Italian game strings must implement. */
export type GameTranslation = DeepString<typeof gameEn>;
